import { QDataTable } from '@kw/quokka-ui';
import { useMemo, useState } from 'react';

import _ from 'lodash';
import { useFetchService } from '../../utils/fetchService';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { OrganizationDropdown } from './OrganizationDropdown';
import { formatDateTime } from './utils';

type OrgInventoryRecord = {
  orgId: string;
  createdAt: string;
  licenses: string[];
  configuredMdms: string[];
  lastMdmSync: string | null;
};

type AppInventoryRecord = {
  orgId: string;
  totalApps: number;
  iosApps: number;
  androidApps: number;
  lastAppSubmission: string | null;
};

type DeviceInventoryRecord = {
  orgId: string;
  totalDevices: number;
  totalIosDevices: number;
  totalAndroidDevices: number;
};

type InventoryRow = OrgInventoryRecord & Omit<AppInventoryRecord, 'orgId'> & Omit<DeviceInventoryRecord, 'orgId'>;

const EMPTY_ORG: Omit<OrgInventoryRecord, 'orgId' | 'createdAt'> = { licenses: [], configuredMdms: [], lastMdmSync: null };
const EMPTY_APP: Omit<AppInventoryRecord, 'orgId'> = { totalApps: 0, iosApps: 0, androidApps: 0, lastAppSubmission: null };
const EMPTY_DEVICE: Omit<DeviceInventoryRecord, 'orgId'> = { totalDevices: 0, totalIosDevices: 0, totalAndroidDevices: 0 };

const keyByOrgId = <T,>(data: unknown): _.Dictionary<T> => _.keyBy(data ? _.castArray(data as T) : [], 'orgId');

const renderDate = (value: string | null) => (value ? formatDateTime(value) : 'N/A');
const renderList = (value: string[]) => (value.length ? value.join(', ') : 'N/A');

export const Inventory = () => {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all');

  const isAll = !selectedOrganization || selectedOrganization === 'all';
  const orgSuffix = isAll ? '' : `/${selectedOrganization}`;

  // org-service → Org ID, Created At, Licenses, Configured MDMs, Last MDM Sync
  const { data: orgInventoryData } = useFetchService(`/organizations/inventory${orgSuffix}`, accessToken);
  // eval-service → Total Apps, iOS Apps, Android Apps, Last App Submission
  const { data: appInventoryData } = useFetchService(`/eval/inventory/apps${orgSuffix}`, accessToken);
  // device-service → Total Devices, Total iOS Devices, Total Android Devices
  const { data: deviceInventoryData } = useFetchService(`/device/inventory/devices${orgSuffix}`, accessToken);

  const { data: organizations = [] } = useFetchService('/organizations/list', accessToken);

  const inventoryRows: InventoryRow[] = useMemo(() => {
    const targetOrgs = isAll ? organizations : organizations.filter(o => o.id === selectedOrganization);
    const baseOrgs = targetOrgs.length ? targetOrgs : isAll ? [] : [{ id: selectedOrganization, createdAt: undefined }];

    const orgByOrgId = keyByOrgId<OrgInventoryRecord>(orgInventoryData);
    const appByOrgId = keyByOrgId<AppInventoryRecord>(appInventoryData);
    const deviceByOrgId = keyByOrgId<DeviceInventoryRecord>(deviceInventoryData);

    return baseOrgs.map(org => {
      const orgRec = orgByOrgId[org.id];
      const appRec = appByOrgId[org.id];
      const deviceRec = deviceByOrgId[org.id];
      return {
        orgId: org.id,
        createdAt: orgRec?.createdAt ?? org.createdAt,
        licenses: orgRec?.licenses ?? EMPTY_ORG.licenses,
        configuredMdms: orgRec?.configuredMdms ?? EMPTY_ORG.configuredMdms,
        lastMdmSync: orgRec?.lastMdmSync ?? EMPTY_ORG.lastMdmSync,
        totalApps: appRec?.totalApps ?? EMPTY_APP.totalApps,
        iosApps: appRec?.iosApps ?? EMPTY_APP.iosApps,
        androidApps: appRec?.androidApps ?? EMPTY_APP.androidApps,
        lastAppSubmission: appRec?.lastAppSubmission ?? EMPTY_APP.lastAppSubmission,
        totalDevices: deviceRec?.totalDevices ?? EMPTY_DEVICE.totalDevices,
        totalIosDevices: deviceRec?.totalIosDevices ?? EMPTY_DEVICE.totalIosDevices,
        totalAndroidDevices: deviceRec?.totalAndroidDevices ?? EMPTY_DEVICE.totalAndroidDevices,
      };
    });
  }, [isAll, organizations, selectedOrganization, orgInventoryData, appInventoryData, deviceInventoryData]);

  const columns = useMemo(
    () => [
      { field: 'orgId', headerName: 'Org ID' },
      { field: 'createdAt', headerName: 'Created At', renderCell: row => renderDate(row.createdAt) },
      { field: 'licenses', headerName: 'Licenses', renderCell: row => renderList(row.licenses) },
      { field: 'configuredMdms', headerName: 'Configured MDMs', renderCell: row => renderList(row.configuredMdms) },
      { field: 'lastMdmSync', headerName: 'Last MDM Sync', renderCell: row => renderDate(row.lastMdmSync) },
      { field: 'totalApps', headerName: 'Total Apps' },
      { field: 'iosApps', headerName: 'iOS Apps' },
      { field: 'androidApps', headerName: 'Android Apps' },
      { field: 'lastAppSubmission', headerName: 'Last App Submission', renderCell: row => renderDate(row.lastAppSubmission) },
      { field: 'totalDevices', headerName: 'Total Devices' },
      { field: 'totalIosDevices', headerName: 'Total iOS Devices' },
      { field: 'totalAndroidDevices', headerName: 'Total Android Devices' },
    ],
    [],
  );

  return (
    <>
      <OrganizationDropdown setSelectedOrganization={setSelectedOrganization} hasAllOption defaultOrganization="all" />

      <QDataTable emptyTableText="No Inventory Found" columns={columns} data={inventoryRows} searchConfig={['orgId']} />
    </>
  );
};
